import type { DocumentView } from "@procuremate/shared";
import { useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { CircleAlert, CircleCheck, FileText, LoaderCircle, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { requestKeys } from "./api";
import { precheck, uploadDocument, uploadProblem } from "./documents-api";

interface Upload {
  key: string;
  name: string;
  progress: number;
  problem: string | null;
}

/**
 * Uploads in progress for one request (WP-2.2): checked in the browser first, sent one by one
 * with progress, then the request is re-read so the document appears with its status. Failed
 * uploads stay listed with their reason until dismissed.
 */
export function useAttachments(requestId: string, attachedCount: number) {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<Upload[]>([]);
  const controllers = useRef(new Set<AbortController>());
  const count = useRef(attachedCount);
  count.current = attachedCount;

  // Leaving the page stops uploads still in flight.
  useEffect(
    () => () => {
      for (const c of controllers.current) c.abort();
    },
    [],
  );

  const add = useCallback(
    async (files: Iterable<File>) => {
      const update = (key: string, change: Partial<Upload>) =>
        setUploads((list) => list.map((u) => (u.key === key ? { ...u, ...change } : u)));
      let pending = count.current;
      for (const file of files) {
        const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
        const problem = precheck(file, pending);
        setUploads((list) => [...list, { key, name: file.name, progress: 0, problem }]);
        if (problem) continue;
        pending += 1;
        const controller = new AbortController();
        controllers.current.add(controller);
        try {
          await uploadDocument(requestId, file, (progress) => update(key, { progress }), controller.signal);
          setUploads((list) => list.filter((u) => u.key !== key));
          await queryClient.invalidateQueries({ queryKey: requestKeys.detail(requestId) });
        } catch (error) {
          if (controller.signal.aborted) return;
          pending -= 1;
          update(key, { problem: uploadProblem(error) });
        } finally {
          controllers.current.delete(controller);
        }
      }
    },
    [queryClient, requestId],
  );

  const dismiss = (key: string) => setUploads((list) => list.filter((u) => u.key !== key));
  return { uploads, add, dismiss };
}

/** Above the composer: uploads in progress or refused, and the request's documents as chips. */
export function AttachmentTray({
  uploads,
  documents,
  onDismiss,
}: {
  uploads: Upload[];
  documents: DocumentView[];
  onDismiss: (key: string) => void;
}) {
  const { t } = useTranslation();
  if (uploads.length === 0 && documents.length === 0) return null;
  return (
    <div className="mb-2 flex flex-col gap-1.5">
      {uploads.map((u) =>
        u.problem ? (
          <p key={u.key} role="alert" className="flex items-start gap-1.5 text-sm text-danger">
            <CircleAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 break-words">
              <span className="font-semibold">{u.name}</span>: {t(`requests.documents.problems.${u.problem}`)}
            </span>
            <button
              type="button"
              className="rounded-sm p-0.5 text-fg-muted hover:bg-bg"
              aria-label={t("requests.documents.dismiss", { name: u.name })}
              onClick={() => onDismiss(u.key)}
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          </p>
        ) : (
          <div key={u.key} className="text-sm">
            <p className="flex items-center gap-1.5 text-fg">
              <LoaderCircle aria-hidden="true" className="h-4 w-4 shrink-0 animate-spin text-primary" />
              <span className="min-w-0 truncate">{t("requests.documents.uploading", { name: u.name })}</span>
            </p>
            <div
              role="progressbar"
              aria-label={t("requests.documents.uploading", { name: u.name })}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(u.progress * 100)}
              className="mt-1 h-1.5 overflow-hidden rounded-full bg-bg"
            >
              <div
                className="h-full bg-primary transition-[width]"
                style={{ width: `${Math.round(u.progress * 100)}%` }}
              />
            </div>
          </div>
        ),
      )}
      {documents.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={t("requests.documents.attached")}>
          {documents.map((d) => (
            <li
              key={d.id}
              className={clsx(
                "inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
                d.status === "FAILED" ? "border-danger text-danger" : "border-border text-fg",
              )}
            >
              <DocumentStatusIcon status={d.status} />
              <span className="min-w-0 truncate">{d.fileName}</span>
              <span className="sr-only">: {t(`requests.documents.status.${d.status}`)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function DocumentStatusIcon({ status }: { status: DocumentView["status"] }) {
  if (status === "READY") return <CircleCheck aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-success" />;
  if (status === "FAILED") return <CircleAlert aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-danger" />;
  if (status === "PROCESSING" || status === "PENDING")
    return <LoaderCircle aria-hidden="true" className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />;
  return <FileText aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />;
}
